/**
 * ImportVentesBatch — Upload en masse de factures de vente historiques.
 *
 * Utilise l'API async /import-jobs : un seul POST envoie tous les fichiers,
 * le serveur crée un job et traite en arrière-plan. Le frontend poll toutes
 * les 2s pour rafraîchir la progression. L'utilisateur peut fermer l'onglet
 * — le job continue et est retrouvable depuis la page « Historique imports ».
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Upload, FileText, CheckCircle2, XCircle, Loader2, ArrowLeft,
  Download, History, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import QuickReviewDraftDialog from "@/components/imports/QuickReviewDraftDialog";
import { uploadImportJob, MAX_IMPORT_FILES } from "@/lib/importJobsUpload";

export default function ImportVentesBatch({ embedded = false } = {}) {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [sent, setSent] = useState(null);
  const [reviewId, setReviewId] = useState(null);
  const pollRef = useRef(null);

  const onPick = (list) => {
    const arr = Array.from(list || []).filter(
      (f) => (f.type === "application/pdf" || f.type.startsWith("image/"))
              && f.size <= 10 * 1024 * 1024);
    if (arr.length > MAX_IMPORT_FILES) {
      toast.warning(`Maximum ${MAX_IMPORT_FILES} fichiers : seuls les ${MAX_IMPORT_FILES} premiers sont gardés`);
    }
    setFiles(arr.slice(0, MAX_IMPORT_FILES));
    setJob(null);
  };

  const start = async () => {
    setStarting(true);
    try {
      const j = await uploadImportJob("ventes", files, (done, total) => setSent({ done, total }));
      setJob(j);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setStarting(false);
      setSent(null);
    }
  };

  // Polling
  useEffect(() => {
    if (!job || job.status === "done" || job.status === "cancelled") {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/import-jobs/${job.id}`);
        setJob(r.data.job);
      } catch { /* keep polling */ }
    }, 2000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [job?.id, job?.status]);

  const cancel = async () => {
    if (!job) return;
    try {
      await api.post(`/import-jobs/${job.id}/cancel`);
      toast.info("Annulation demandée…");
    } catch (e) { toast.error(apiError(e)); }
  };

  const progress = job ? Math.round((job.done / Math.max(1, job.total)) * 100) : 0;

  const exportReport = () => {
    if (!job) return;
    const lines = [["Fichier", "Statut", "Numéro extrait", "Erreur", "ID facture"]];
    job.items.forEach((r) => lines.push([r.filename, r.status,
      r.extracted_number || "", r.error || "", r.invoice_id || ""]));
    const csv = lines.map((l) => l.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `import-ventes-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="import-ventes-batch-page">
      <div className="flex items-center justify-between">
        <Link to="/app/invoices">
          <Button variant="ghost" size="sm" className="text-slate-500">
            <ArrowLeft className="h-4 w-4 mr-1" /> Factures émises
          </Button>
        </Link>
        <Link to="/app/import-jobs">
          <Button variant="outline" size="sm" data-testid="link-import-history-btn">
            <History className="h-4 w-4 mr-1" /> Historique imports
          </Button>
        </Link>
      </div>
      <div>
        {!embedded && (
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            Import ventes en masse
          </h1>
        )}
        <p className="mt-1 text-sm text-slate-500">
          Upload jusqu'à 1 000 PDF d'un coup — le traitement OCR Gemini se fait
          en arrière-plan côté serveur. Fermez l'onglet si vous voulez, le job
          continue.
        </p>
      </div>

      {!files.length && !job && (
        <Card>
          <label className="block cursor-pointer p-12 text-center border-2 border-dashed rounded-xl
                             border-slate-200 hover:border-blue-400 hover:bg-blue-50/30 transition-colors"
                  data-testid="batch-upload-zone">
            <input id="batch-file-input" name="batch-file-input" type="file" accept="application/pdf,image/*" multiple className="hidden"
                    onChange={(e) => onPick(e.target.files)}
                    data-testid="batch-file-input" />
            <Upload className="h-10 w-10 mx-auto text-slate-400 mb-3" />
            <p className="text-sm font-medium text-slate-700">
              Glisse plusieurs PDF ici ou clique pour sélectionner
            </p>
            <p className="text-xs text-slate-400 mt-1">PDF ou images, 10 Mo par fichier, 1 000 max</p>
          </label>
        </Card>
      )}

      {files.length > 0 && !job && (
        <Card><CardContent className="p-4 flex items-center justify-between">
          <div className="text-sm text-slate-700">
            <b>{files.length}</b> fichier(s) prêt(s) à envoyer
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setFiles([])}>
              Réinitialiser
            </Button>
            <Button onClick={start} disabled={starting}
                     className="bg-blue-600 hover:bg-blue-700 text-white"
                     data-testid="batch-run-btn">
              {starting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                          : <Upload className="h-4 w-4 mr-1.5" />}
              {sent ? `Envoi ${sent.done}/${sent.total}…` : "Démarrer l'import"}
            </Button>
          </div>
        </CardContent>
        {sent && (
          <div className="px-4 pb-4" data-testid="batch-upload-progress">
            <Progress value={Math.round((sent.done / Math.max(1, sent.total)) * 100)} />
            <p className="mt-1 text-xs text-slate-500">Envoi des fichiers par paquets — ne fermez pas l'onglet avant la fin de l'envoi.</p>
          </div>
        )}
        </Card>
      )}

      {job && (
        <>
          <Card><CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-700">
                Job <span className="font-mono text-xs text-slate-500">
                  {job.id.slice(0, 8)}</span> — {job.done}/{job.total} traités
                {" · "}<b className="font-mono">{progress}%</b>
                {" · statut "}
                <Badge variant="outline" className={
                  job.status === "done" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : job.status === "processing" ? "bg-blue-50 text-blue-700 border-blue-200"
                  : "bg-slate-50 text-slate-600"}
                  data-testid="job-status-badge">
                  {({done:"Terminé",processing:"En cours",cancelled:"Annulé",pending:"En attente"}[job.status]) || job.status}
                </Badge>
              </div>
              <div className="flex gap-2">
                {job.status === "processing" && (
                  <Button variant="outline" size="sm" onClick={cancel}
                          data-testid="batch-cancel-btn">
                    Annuler
                  </Button>
                )}
                {job.status === "done" && (
                  <>
                    <Button variant="outline" size="sm" onClick={exportReport}
                             data-testid="batch-export-report-btn">
                      <Download className="h-4 w-4 mr-1.5" /> Rapport CSV
                    </Button>
                    <Button onClick={() => navigate("/app/invoices")}
                             className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      Voir les brouillons
                    </Button>
                  </>
                )}
              </div>
            </div>
            <Progress value={progress} />
            <div className="flex gap-3 text-xs">
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200"
                      data-testid="batch-stat-success">
                ✓ Succès : {job.success || 0}
              </Badge>
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200"
                      data-testid="batch-stat-error">
                ✗ Erreurs : {job.error || 0}
              </Badge>
              <Badge variant="outline" className="bg-slate-50 text-slate-600">
                En attente : {job.total - job.done}
              </Badge>
              {job.status === "done" && job.alerts_count > 0 && (
                <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200"
                       data-testid="batch-stat-alerts">
                  ⚠ {job.alerts_count} alerte(s) post-import
                </Badge>
              )}
            </div>
          </CardContent></Card>

          <Card>
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead>Fichier</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>N° extrait</TableHead>
                <TableHead>Détail</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {job.items.map((r, i) => (
                  <TableRow key={i} data-testid={`batch-row-${i}`}>
                    <TableCell className="text-sm truncate max-w-xs">
                      <FileText className="h-3.5 w-3.5 inline mr-1 text-slate-400" />
                      {r.filename}
                    </TableCell>
                    <TableCell>
                      {r.status === "pending" && <Badge variant="outline">En attente</Badge>}
                      {r.status === "processing" && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          <Loader2 className="h-3 w-3 mr-1 animate-spin inline" /> Traitement
                        </Badge>
                      )}
                      {r.status === "success" && (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          <CheckCircle2 className="h-3 w-3 mr-1 inline" /> Créé
                        </Badge>
                      )}
                      {r.status === "error" && (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          <XCircle className="h-3 w-3 mr-1 inline" /> Échec
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{r.extracted_number || "—"}</TableCell>
                    <TableCell className="text-xs">
                      {r.invoice_id && r.status_created === "draft" && (
                        <div className="flex items-center gap-2">
                          <button type="button"
                                  onClick={() => setReviewId(r.invoice_id)}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs"
                                  data-testid={`batch-review-${i}`}>
                            <Pencil className="w-3 h-3" /> Réviser
                          </button>
                          <Link to={`/app/invoices/${r.invoice_id}/edit`}
                                 className="text-blue-600 hover:underline">
                            Ouvrir le brouillon
                          </Link>
                        </div>
                      )}
                      {r.invoice_id && r.status_created !== "draft" && (
                        <Link to={`/app/invoices/${r.invoice_id}/edit`}
                               className="text-blue-600 hover:underline">
                          Ouvrir la facture
                        </Link>
                      )}
                      {r.error && <span className="text-red-600">{r.error}</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {job.status === "done" && job.alerts_count > 0 && (
            <Alert className="border-amber-200 bg-amber-50">
              <AlertDescription className="text-sm text-amber-800">
                <b>{job.alerts_count} alerte(s)</b> post-import détectée(s).{" "}
                <Link to={`/app/import-jobs?job=${job.id}`} className="underline">
                  Voir le détail dans l'historique imports →
                </Link>
              </AlertDescription>
            </Alert>
          )}
          {job.status === "done" && job.error > 0 && (
            <Alert className="border-red-200 bg-red-50">
              <AlertDescription className="text-sm text-red-800">
                {job.error} fichier(s) en échec — télécharge le rapport CSV et
                retente-les via <Link to="/app/invoices/import" className="underline">
                Import PDF unitaire</Link>.
              </AlertDescription>
            </Alert>
          )}
        </>
      )}

      <QuickReviewDraftDialog
        invoiceId={reviewId}
        open={!!reviewId}
        onOpenChange={(o) => !o && setReviewId(null)}
        onSaved={async () => {
          // Refresh job pour voir les items à jour (extracted_number peut changer)
          if (job?.id) {
            try {
              const r = await api.get(`/import-jobs/${job.id}`);
              setJob(r.data.job);
            } catch { /* silent */ }
          }
        }}
      />
    </div>
  );
}
